import type { OutreachTemplate, OutreachTemplateGroups, OutreachTemplateInput, OutreachTemplateLintWarning } from '@discovr/types';
import type { PatchTemplateVars } from '@discovr/features';
import { canonicalHtml } from '../composerEditor/stripHtmlAttributes';
import { defaultSequenceTemplate, effectiveSequenceTemplate, templateSections } from '../defaultSequenceTemplate';
import { defaultTemplateName, toStepDrafts, toTemplateSteps, type TemplateStepDraft } from '../templateValidation';

/** The template a pick resolves to (archived included, so a saved pick still shows); no pick means the global default. */
export function resolveTemplate(groups: OutreachTemplateGroups | undefined, pickedId: number | null): OutreachTemplate | undefined {
  if (!groups) return undefined;
  return effectiveSequenceTemplate(groups, pickedId);
}

/** The template step's choices: the Discovr default, then live brand and campaign sequence templates. */
export function templateChoices(groups: OutreachTemplateGroups): OutreachTemplate[] {
  const fallback = defaultSequenceTemplate(groups);
  return [...(fallback ? [fallback] : []), ...templateSections(groups).flatMap((section) => section.rows)];
}

export function stepsMatchTemplate(steps: TemplateStepDraft[], template: OutreachTemplate): boolean {
  const saved = toStepDrafts(template.steps);
  return (
    steps.length === saved.length &&
    steps.every((step, i) => {
      const other = saved[i];
      return !!other && step.delayDays === other.delayDays && step.subject === other.subject && step.body === other.body && step.sendTime === other.sendTime && canonicalHtml(step.bodyHtml) === canonicalHtml(other.bodyHtml);
    })
  );
}

export interface BatchTemplateSaver {
  create: (input: OutreachTemplateInput) => Promise<OutreachTemplate>;
  patch: (vars: PatchTemplateVars) => Promise<OutreachTemplate>;
}

export interface CommittedTemplate {
  templateId: number;
  saved: 'unchanged' | 'created' | 'versioned';
  /** The save's deliverability lint, advisory only; empty when nothing was saved. */
  warnings: OutreachTemplateLintWarning[];
}

/**
 * Settles which template this batch drafts from, saving the step-2 edits to the Templates tab first.
 * Untouched: the picked template as is. An edited campaign template saves a new version of itself.
 * An edited global or brand one is never changed in place (the global stays single-step, a brand one is
 * shared with other campaigns): the edit is saved as a NEW campaign template, with the name left as the
 * default so the server adds " (2)" on a clash. A failed save rejects, so drafting never starts on it.
 */
export async function commitBatchTemplate(template: OutreachTemplate, steps: TemplateStepDraft[], saver: BatchTemplateSaver): Promise<CommittedTemplate> {
  if (stepsMatchTemplate(steps, template)) return { templateId: template.id, saved: 'unchanged', warnings: [] };
  const wire = toTemplateSteps(steps);
  if (template.scope === 'campaign') {
    const updated = await saver.patch({ templateId: template.id, patch: { steps: wire } });
    return { templateId: updated.id, saved: 'versioned', warnings: updated.warnings ?? [] };
  }
  const created = await saver.create({
    scope: 'campaign',
    kind: 'sequence',
    name: defaultTemplateName(steps[0]?.subject ?? ''),
    nameIsDefault: true,
    steps: wire,
    parentTemplateId: template.id,
  });
  return { templateId: created.id, saved: 'created', warnings: created.warnings ?? [] };
}
